import { fetchData  } from "../libs/util.ts";
import { create } from "zustand";
import { getAuth } from "firebase/auth";
import { firebaseApp } from "../libs/firebase.js";
import { getPricesAndLocationsAPIUrl } from "./config.js";
import * as Sentry from "@sentry/react";

export enum Plans { yearly = 'yearly', monthly = 'monthly' }
export enum UserTypes { student = 'student', pro = 'pro', adopter = 'adopter' }
export enum LookupKey {
    pro_monthly = 'pro_monthly',
    pro_yearly = 'pro_yearly',
    student_monthly = 'student_monthly',
    student_yearly = 'student_yearly',
    // Per-seat team Prices; /getPricesAndLocation lists them under these keys.
    team_monthly = 'team_monthly',
    team_yearly = 'team_yearly'
}
export interface ProfilePrices {
    productID: string | null,
    priceID:string | null,
    planType: Plans,
    userType: UserTypes,
    currency: string,
    lookup_key: LookupKey,
    local_price: number | null,
}
// A failed price load retries itself this many times (1s, 2s, 4s), then stops until
// something asks again; it used to retry every second, forever, with a Sentry event each time.
const MAX_AUTO_RETRIES = 3

export const useStripeStore = create( (set:any, get:any) => ({
    local_currency:null,
    stripe_prices: null,
    profile_prices:{},
    processing:false,
    failures:0,
    loadId:0,

    // `force` is a user's explicit retry: it starts a fresh request even while an
    // earlier one hangs or answered without a plan, and resets the automatic-retry budget.
    setProductAndPrices: async ({ force = false }: { force?: boolean } = {}) => {
        let stripe_prices = get().stripe_prices
        let currency = get().local_currency
        set({profile_prices:get().generateProfilePrices(stripe_prices,currency ) })

        // One load at a time, and none once prices are in, unless forced.
        if (get().processing==true && !force) return
        const loadId = get().loadId + 1
        set({processing:true, loadId, ...(force ? { failures: 0 } : {})})
        const queryParams = new URLSearchParams(window.location.search);
        const forceCurrency = queryParams.get('forcecurrency');
        // The bearer token lets the server pick regional pricing from
        // profile.country (regional countries → $49). Logged-out or token failures fall
        // back to the request's IP country.
        let authHeaders = {}
        try {
            const user = getAuth(firebaseApp).currentUser
            if (user) authHeaders = { Authorization: `Bearer ${await user.getIdToken()}` }
        } catch (e) { /* falls back to IP-country pricing */ }
        const resp = await fetchData( {}, getPricesAndLocationsAPIUrl, authHeaders)
        // A newer load owns the retry state; a late failure from a superseded one is ignored.
        if (!resp?.prices && get().loadId !== loadId) return

        if (forceCurrency) { currency = forceCurrency.toLowerCase(); }
        else { currency = resp?.location?.currency?.code?.toLowerCase() || 'usd' }
        set({ local_currency: currency });

        if (!resp?.prices) {
            const failures = get().failures + 1
            set({processing:false, failures})
            if (failures <= MAX_AUTO_RETRIES) {
                // Skipped if a newer load has started or a late answer brought prices in.
                setTimeout(() => {
                    if (get().loadId === loadId && !get().stripe_prices) get().setProductAndPrices()
                }, 1000 * 2 ** (failures - 1))
            } else if (failures == MAX_AUTO_RETRIES + 1) {
                Sentry.captureException("No prices found after retries")
            }
            return
        }
        stripe_prices = resp.prices
                
        stripe_prices.data.map( (d) => {
            if (!d.currency_options) d.local_price = d.unit_amount_decimal
            else   {         
                if (d.currency_options[currency]) {
                    d.local_price = d.currency_options[currency]?.unit_amount_decimal   
                    d.local_currency = currency                 
                }       
                else {
                    d.local_price = d.currency_options['usd'].unit_amount_decimal
                    d.local_currency = 'usd'
                }
            }
        })
        if (window?.debug) window.debug.stripe_prices = stripe_prices 
        set({stripe_prices:stripe_prices})
        set({profile_prices:get().generateProfilePrices(stripe_prices, currency ) })
    },

    generateProfilePrices:(stripe_prices,currency='usd') => {
        const profilePrices={}
        const lookupKeys = Object.values(LookupKey)
        lookupKeys.map( (lookupKey) => {
            const profilePrice = get().createProfilePrices(lookupKey,stripe_prices,currency)
            profilePrices[lookupKey] = profilePrice
        })
        // A server without team keys prices a seat at Pro, so show Pro.
        const teamFallback = [[LookupKey.team_monthly, LookupKey.pro_monthly], [LookupKey.team_yearly, LookupKey.pro_yearly]]
        teamFallback.map( ([team, pro]) => {
            if (!profilePrices[team].priceID) profilePrices[team] = { ...profilePrices[pro], lookup_key: team }
        })
        return profilePrices
    },

    createProfilePrices: (lookupKey:LookupKey,stripe_prices,currency):ProfilePrices => {
        let userType:UserTypes=UserTypes.pro
        let planType:Plans = Plans.monthly
        if (lookupKey==LookupKey.pro_monthly) {userType = UserTypes.pro; planType = Plans.monthly }
        else if (lookupKey==LookupKey.pro_yearly) {
            userType = UserTypes.pro; planType = Plans.yearly
        }
        else if (lookupKey==LookupKey.student_monthly) {
            userType = UserTypes.student; planType = Plans.monthly
        }
        else if (lookupKey==LookupKey.student_yearly) { userType = UserTypes.student; planType = Plans.yearly }
        else if (lookupKey==LookupKey.team_yearly) { planType = Plans.yearly }
        const profilePrice:ProfilePrices = {
            productID: null,priceID: null, planType: planType, userType: userType,
            currency: currency, lookup_key: lookupKey,
            local_price: null
        } as ProfilePrices
        if (!stripe_prices) return profilePrice
    
        const price = stripe_prices.data.filter(d => d.lookup_key==lookupKey)?.[0]
        if (!price?.local_price) return profilePrice
        profilePrice.local_price = price.local_price
        profilePrice.currency = price.local_currency
        profilePrice.priceID = price.id
        profilePrice.productID = price.product
        return profilePrice
    },
  }))